import { useCallback, useEffect, useState } from 'react';

import "./styles.css";

// material-ui
import { Box, Toolbar, Typography, Button, Tooltip, Tabs, Tab } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DashboardOutlined, GpsFixedOutlined, NotificationsActive } from '@mui/icons-material';

import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';

import { SelectedParametersType, UserType } from '../../../../types/types';
import { userAtom } from '../../../../atoms/atoms';
import findAllData from '../../../../resources/Api/dashboard/findAllData';
import { useCookies } from 'react-cookie';
import { oldTotalTicketsUnresolvedLocalStorageKey } from '../../../../consts/consts';

import TrackingTab from './TrackingTab';
import SystemTab from './SystemTab';

const Dashboard = () => {
  const getCurrentUser = useRecoilValue<UserType>(userAtom);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);

  const [isLoading, setLoading] = useState(false);
  const [totalCustomersData, setTotalCustomersData] = useState<Array<number>>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [totalDriverCount, setTotalDriverCount] = useState<Array<number>>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [totalVehiclesCount, setTotalVehiclesCount] = useState<Array<number>>([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
  const [totalTicketsResolved, setTotalTicketsResolved] = useState<number>(0);
  const [totalTicketsUnresolved, setTotalTicketsUnresolved] = useState<number>(0);
  const [totalDriversPerCnh, setTotalDriversPerCnh] = useState<Array<{}>>([{}]);
  const [totalVehiclesPerBrand, setTotalVehiclesPerBrand] = useState<Array<{}>>([{}]);
  const [totalTicketsByGravity, setTotalTicketsByGravity] = useState<Array<{}>>([{}]);

  const [totalVehiclesByCustomer, setTotalVehiclesByCustomer] = useState<Array<{}>>([{}]);
  const [totalDevicesByManufacturer, setTotalDevicesByManufacturer] = useState<Array<{}>>([{}]);
  const [totalVehiclesByStatus, setTotalVehiclesByStatus] = useState<Array<{}>>([{}]);

  const [hasNewTicketUnresolved, setHasNewTicketUnresolved] = useState(false);

  const [cookies, _] = useCookies(["selectedParameters", "token"]);

  const getDashboardData = useCallback(
    async (status: string = "activated") => {
      const token: string = cookies.token || sessionStorage.getItem("token");
      const currentParameters: SelectedParametersType = cookies.selectedParameters;

      setLoading(true);
      try {
        const { data } = await findAllData(token, currentParameters?.customer?.id);

        setTotalCustomersData(data.totalCustomersCount);
        setTotalDriverCount(data.totalDriverCount);
        setTotalVehiclesCount(data.totalVehiclesCount);
        setTotalTicketsResolved(data.totalTicketsResolved);
        setTotalDriversPerCnh(data.totalDriversPerCnh);
        setTotalVehiclesPerBrand(data.totalVehiclesPerBrand);
        setTotalTicketsByGravity(data.totalTicketsByGravity);
        setTotalVehiclesByCustomer(data.totalVehiclesByCustomer);
        setTotalDevicesByManufacturer(data.totalDevicesByManufacturer);
        setTotalVehiclesByStatus(data.totalVehiclesByStatus);

        const totalUnresolved = data.totalTicketsUnresolved;
        const userLocalStorageKey = `${oldTotalTicketsUnresolvedLocalStorageKey}_${getCurrentUser.id}`;
        const oldTotalStr = localStorage.getItem(userLocalStorageKey);
        setTotalTicketsUnresolved(totalUnresolved);

        if (oldTotalStr === null) {
          localStorage.setItem(userLocalStorageKey, totalUnresolved.toString());
        } else {
          const oldTotal = Number(oldTotalStr);
          if (!isNaN(oldTotal) && totalUnresolved > oldTotal) {
            setHasNewTicketUnresolved(true);
          } else if (isNaN(oldTotal)) {
            localStorage.removeItem(userLocalStorageKey);
          }
        }

      } catch (error: any) {
        console.log(error);
        setTotalCustomersData([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        setTotalDriverCount([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        setTotalVehiclesCount([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
        setTotalTicketsResolved(0);
        setTotalTicketsUnresolved(0);
        setTotalDriversPerCnh([{}]);
        setTotalVehiclesPerBrand([{}]);
        setTotalTicketsByGravity([{}]);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    },
    [cookies.token, cookies.selectedParameters, getCurrentUser.id]
  );

  useEffect(() => {
    getDashboardData();
  }, [getDashboardData]);

  return (
    <Box
      className="dashboard-root grid-box-out-container container item tw-w-full tw-min-w-0 tw-bg-slate-50 tw-p-2 dark:tw-bg-slate-950 sm:tw-p-3"
      style={{
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: 8,
        background: "#f9fafb",
      }}
    >
      <>
        <Toolbar
          className="grid-header-bar tw-mb-3 tw-rounded-xl tw-bg-gtr-sidebar tw-text-white tw-shadow-sm"
          style={{
            display: "grid",
            backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
            fontFamily: "Prometo",
            borderRadius: 12,
            marginBottom: 12,
            color: "#ffff",
          }}
        >
          <Box
            className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-justify-between tw-gap-2"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              className="header-actions tw-min-w-0"
              sx={{
                display: "flex",
                alignItems: "center",
                alignContent: "center",
                fontFamily: "Prometo",
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: "Prometo", overflowWrap: 'anywhere' }}>
                Bem vindo de volta, {getCurrentUser.name}!
              </Typography>
            </Box>

            {hasNewTicketUnresolved && (
              <Tooltip title="Há novos chamados pendentes" arrow>
                <Button
                  variant="contained"
                  component={Link}
                  to="/help-desk"
                  state={{ tab: 2 }}
                  onClick={() => {
                    setHasNewTicketUnresolved(false);
                  }}
                  disableElevation
                  color="warning"
                  startIcon={<NotificationsActive />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontFamily: 'Prometo',
                    whiteSpace: 'nowrap',
                    width: '45px',
                    minWidth: '45px',
                    flexShrink: 0,
                    overflow: 'hidden',
                    transition: 'width 0.4s ease, padding 0.4s ease',
                    justifyContent: 'flex-start',
                    padding: '8px 10px',
                    '&:hover': {
                      width: { xs: '45px', sm: '320px' },
                      padding: { xs: '8px 10px', sm: '8px 20px' },
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: '15px',
                      marginLeft: '2px'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '13px' }}>
                    Há chamados novos para visualização
                  </Typography>
                </Button>
              </Tooltip>
            )}
          </Box>
        </Toolbar>

        <Toolbar
          className="grid-header-bar tw-mb-3 tw-overflow-hidden tw-rounded-xl tw-bg-gtr-sidebar tw-text-white tw-shadow-sm"
          sx={{
            display: "flex",
            backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
            fontFamily: "Prometo",
            minHeight: 48,
            borderRadius: 3,
            mb: 1.5,
            color: "#fff",
            px: 1,
          }}
        >
          <Box
            className="header-actions"
            sx={{
              display: "flex",
              alignItems: "center",
              overflowX: "auto",
              width: "100%",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_e, value) => setActiveTab(value)}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              indicatorColor="primary"
              textColor="inherit"
              sx={{
                fontFamily: "Prometo",
                color: "#fff",
                width: "100%",
                minHeight: 48,
                "& .MuiTabs-indicator": { height: 3, borderRadius: 3, backgroundColor: "#00B700" },
                "& .MuiTab-root": {
                  minHeight: 48,
                  border: "none",
                  borderRadius: 2,
                  fontFamily: "Prometo",
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "none",
                  color: "rgba(255,255,255,0.72)",
                  transition: "color 180ms ease, background-color 180ms ease",
                },
                "& .MuiTab-root:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" },
                "& .Mui-selected": { color: "#fff !important", backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              <Tab
                icon={<DashboardOutlined />}
                aria-label="system-tab" label={isMobile ? "" : "Visão geral"}
              />
              <Tab
                icon={<GpsFixedOutlined />}
                aria-label="tracking-tab" label={isMobile ? "" : "Operacional"}
              />
            </Tabs>
          </Box>
        </Toolbar>

        {activeTab === 0 ? (
          <SystemTab
            isLoading={isLoading}
            totalCustomersData={totalCustomersData}
            totalDriverCount={totalDriverCount}
            totalVehiclesCount={totalVehiclesCount}
            totalTicketsResolved={totalTicketsResolved}
            totalTicketsUnresolved={totalTicketsUnresolved}
            totalDriversPerCnh={totalDriversPerCnh}
            totalVehiclesPerBrand={totalVehiclesPerBrand}
            totalTicketsByGravity={totalTicketsByGravity}
            totalVehiclesByCustomer={totalVehiclesByCustomer}
            totalDevicesByManufacturer={totalDevicesByManufacturer}
            totalVehiclesByStatus={totalVehiclesByStatus}
          />
        ) : (
          <TrackingTab />
        )}
      </>
    </Box>
  );
};

export default Dashboard;
